import React, { useEffect, useState } from 'react';
import { Button, Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaList, FaChartBar, FaTasks } from 'react-icons/fa';
import api from '../../../api/apiClient';

const initialForm = {
  title: '',
  description: '',
  duration_minutes: 30,
  pass_percentage: 40,
  negative_marking_enabled: false,
  negative_marking_percentage: 0
};

const AssessmentList = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const response = await api.assessments.getAll();
      setAssessments(response.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessments();
  }, []);

  const handleOpenModal = (assessment = null) => {
    setEditing(assessment);
    setFormData(assessment ? {
      title: assessment.title || '',
      description: assessment.description || '',
      duration_minutes: assessment.duration_minutes || 30,
      pass_percentage: assessment.pass_percentage || 40,
      negative_marking_enabled: Boolean(assessment.negative_marking_enabled),
      negative_marking_percentage: assessment.negative_marking_percentage || 0
    } : initialForm);
    setShowModal(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Assessment title is required');
      return;
    }

    try {
      setSaving(true);
      if (editing) {
        await api.assessments.update(editing.id, formData);
        toast.success('Assessment updated successfully');
      } else {
        await api.assessments.create(formData);
        toast.success('Assessment created successfully');
      }
      setShowModal(false);
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.assessments.delete(id);
      toast.success('Assessment deleted successfully');
      fetchAssessments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete assessment');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-600 border-t-transparent"></div></div>;
  }

  return (
    <div className="p-4 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <h5 className="mb-0 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Manage Assessments</h5>
        <Button
          bsPrefix="custom-hamburger"
          onClick={() => handleOpenModal()}
          className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
        >
          <FaPlus /> Add Assessment
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl shadow-lg bg-white">
        <table className="table table-bordered table-hover mb-0">
          <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <tr>
              <th>Title</th>
              <th>Duration</th>
              <th>Pass %</th>
              <th>Total Marks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assessments.map((assessment) => (
              <tr key={assessment.id}>
                <td>{assessment.title}</td>
                <td>{assessment.duration_minutes} min</td>
                <td>{assessment.pass_percentage}%</td>
                <td>{assessment.total_marks}</td>
                <td>
                  <div className="flex gap-2">
                    <button className="p-1 border-0 border-purple-300 text-purple-600 hover:bg-purple-50 rounded" onClick={() => handleOpenModal(assessment)}><FaEdit /></button>
                    <button className="p-1 border-0 border-red-300 text-red-600 hover:bg-red-50 rounded" onClick={() => handleDelete(assessment.id)}><FaTrash /></button>
                    <button className="p-1 border-0 border-blue-300 text-blue-600 hover:bg-blue-50 rounded" onClick={() => navigate(`/admin/assessments/${assessment.id}/questions`)}><FaList /></button>
                    <button className="p-1 border-0 border-cyan-300 text-cyan-600 hover:bg-cyan-50 rounded" onClick={() => navigate(`/admin/assessments/${assessment.id}/assignments`)}><FaTasks /></button>
                    <button className="p-1 border-0 border-emerald-300 text-emerald-600 hover:bg-emerald-50 rounded" onClick={() => navigate(`/admin/assessments/${assessment.id}/analytics`)}><FaChartBar /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <Modal.Title>{editing ? 'Edit Assessment' : 'Create Assessment'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={12}>
                <Form.Label>Title</Form.Label>
                <Form.Control value={formData.title} onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))} />
              </Col>
              <Col md={12}>
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={3} value={formData.description} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} />
              </Col>
              <Col md={6}>
                <Form.Label>Duration (minutes)</Form.Label>
                <Form.Control type="number" value={formData.duration_minutes} onChange={(e) => setFormData((prev) => ({ ...prev, duration_minutes: Number(e.target.value) }))} />
              </Col>
              <Col md={6}>
                <Form.Label>Pass Percentage</Form.Label>
                <Form.Control type="number" value={formData.pass_percentage} onChange={(e) => setFormData((prev) => ({ ...prev, pass_percentage: Number(e.target.value) }))} />
              </Col>
              <Col md={6}>
                <Form.Check type="checkbox" label="Enable Negative Marking" checked={formData.negative_marking_enabled} onChange={(e) => setFormData((prev) => ({ ...prev, negative_marking_enabled: e.target.checked }))} />
              </Col>
              <Col md={6}>
                <Form.Label>Negative Marking (%)</Form.Label>
                <Form.Control type="number" value={formData.negative_marking_percentage} onChange={(e) => setFormData((prev) => ({ ...prev, negative_marking_percentage: Number(e.target.value) }))} />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => setShowModal(false)}
            className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            Cancel
          </Button>
          <Button
            bsPrefix="custom-hamburger"
            onClick={handleSubmit}
            disabled={saving}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssessmentList;
